import type { StoreApi } from 'zustand'
import type { AdyenAdditionalDetailsState, AdyenCheckoutClient, AdyenFlowActions, AdyenSubmitState } from '../../infrastructure/adyen/types'
import type { createPaymentApiClient } from '../../infrastructure/paymentApiClient'
import type { AdyenPaymentResponse, CreatePaymentParametersRequest, InitPaymentRequest, SubmitAdditionalDetailsRequest } from '../../infrastructure/types'
import type { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import type { AnalyticsClient, Logger } from '@/scopes/common/domain/types'
import type { LocationResolver } from '@/scopes/common/infrastructure/dom/location'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import {
  AdyenErrorCode,
  AdyenErrorCodeValues,
  AdyenTelemetryEvent,
  AdyenTelemetryEventValues,
  buildAdyenReportedError,
  buildAdyenTelemetryInfo,
  extractAdyenPaymentFailureDetails,
  extractAdyenReferences,
  INVALID_SUCCESS_URL_ERROR_NAME,
  resolveAdyenErrorCode,
} from '@/scopes/payment/domain/adyen/policies/adyenTelemetry'
import {
  getAmountWithFeesValue,
  getPaymentMethodConfigKey,
  getPaymentMethodType,
  getSelectedAmount,
  resolvePaymentAmounts,
  resolvePaymentMethodType,
  resolveSelectedAmountType,
  resolveSelectedPaymentMethod,
} from '@/scopes/payment/domain/adyen/policies/paymentSelectors'
import { createPaymentEvent, createPaymentMethodEvent, getPaymentEventContext } from '../analytics'
import { PaymentMethodRow } from '../policies/paymentMethodsView'
import { hidesPaymentOptions, requiresPaymentMethodReselection, resolvePaymentResultFromResultCode } from '../policies/paymentResult'
import { AdyenPaymentStatus, AdyenPaymentStatusValues, PaymentOptions, PaymentResult, PaymentResultValues, PaymentScopePage, PaymentStore } from '../types'

const ADYEN_PROVIDER = 'adyen'
const ONLINE_TRANSACTION_TYPE = 'online'

type PaymentApiClient = Pick<
  ReturnType<typeof createPaymentApiClient>,
  'createPaymentParameters' | 'initPayment' | 'submitAdditionalDetails'
>

export interface ActiveMountState {
  cancelled: boolean
  abortController: AbortController
}

export interface AdyenDynamicValues {
  paymentOptions: PaymentOptions
  selectedAmountType: string
  selectedPaymentMethodType: PaymentMethod | null
  paymentMethodRow: PaymentMethodRow
  customDepositAmount: number | null
}

interface AdyenPaymentActionsDependencies {
  apiClient: PaymentApiClient
  adyenClient: AdyenCheckoutClient
  location: LocationResolver
  logger: Logger
  store: StoreApi<PaymentStore>
  bookingNumber: string
  contextItemId: string
  groupUserId: string | null
  scope?: PaymentScopePage
  getDynamicValues: () => AdyenDynamicValues
  analyticsClient?: AnalyticsClient
}

export function createAdyenPaymentActions({
  apiClient,
  adyenClient,
  location,
  logger,
  store,
  bookingNumber,
  contextItemId,
  groupUserId,
  scope,
  getDynamicValues,
  analyticsClient = ga4analyticsClient,
}: AdyenPaymentActionsDependencies) {
  const context = getPaymentEventContext(scope ?? 'BookingPayment')
  let activeMount: ActiveMountState | null = null
  let adyenPspReference: string | undefined
  let adyenMerchantReference: string | undefined
  let adyenPaymentFailureDetails: ReturnType<typeof extractAdyenPaymentFailureDetails> = {}

  function sendPaymentResult(paymentResult: PaymentResult) {
    analyticsClient.sendEvent(createPaymentEvent({
      description: 'payment result',
      element: 'payment result banner',
      type: getDynamicValues().selectedPaymentMethodType ?? 'unknown',
      result: paymentResult,
      interaction: false,
      context,
    }))
  }

  function completeWith(
    paymentResult: PaymentResult,
    status: AdyenPaymentStatus = AdyenPaymentStatusValues.READY,
    trackResult = true,
  ) {
    const state = store.getState()
    state.setIn('status', status)
    state.setIn('paymentResult', paymentResult)
    if (trackResult) {
      sendPaymentResult(paymentResult)
    }
  }

  function unselectPaymentMethod() {
    store.getState().setIn('selectedPaymentMethodType', null)
  }

  function isAdyenProvider() {
    const { paymentOptions } = getDynamicValues()
    return Boolean(paymentOptions.paymentProvider) && paymentOptions.paymentProvider.toLowerCase() === ADYEN_PROVIDER
  }

  function buildCurrentTelemetryInfo() {
    const { paymentOptions, selectedAmountType, selectedPaymentMethodType } = getDynamicValues()
    const selectedAmount = getSelectedAmount(paymentOptions.paymentAmounts, selectedAmountType)

    return buildAdyenTelemetryInfo({
      bookingNumber,
      paymentMethodBrand: resolvePaymentMethodType(selectedPaymentMethodType) ?? '',
      paymentOption: resolveSelectedAmountType(
        selectedAmountType,
        selectedAmount?.allowsCustomAmount ?? false,
      ),
      pspReference: adyenPspReference,
      merchantReference: adyenMerchantReference,
    })
  }

  function logAdyenEvent(eventName: AdyenTelemetryEvent, info?: Record<string, unknown>) {
    logger.logEvent(eventName, {
      ...buildCurrentTelemetryInfo(),
      ...info,
    })
  }

  function logAdyenError(error: unknown, errorCode: AdyenErrorCode, info?: Record<string, unknown>) {
    logger.logError(buildAdyenReportedError(error, errorCode), {
      ...buildCurrentTelemetryInfo(),
      ...info,
      errorCode,
    })
  }

  function setAdyenReferences(response: AdyenPaymentResponse) {
    const references = extractAdyenReferences(response)

    if (references.pspReference) {
      adyenPspReference = references.pspReference
    }

    if (references.merchantReference) {
      adyenMerchantReference = references.merchantReference
    }
  }

  function buildCreatePaymentParametersRequest(): CreatePaymentParametersRequest {
    const { paymentOptions, selectedAmountType, customDepositAmount, selectedPaymentMethodType } = getDynamicValues()
    const selectedAmount = getSelectedAmount(paymentOptions.paymentAmounts, selectedAmountType)

    const paymentAmounts = resolvePaymentAmounts(
      selectedAmount,
      customDepositAmount,
      selectedAmount?.allowsCustomAmount ?? false,
      selectedPaymentMethodType,
      groupUserId !== null,
    )
    const paymentMethod = resolvePaymentMethodType(selectedPaymentMethodType) ?? ''
    const paymentAmountType = resolveSelectedAmountType(
      selectedAmountType,
      selectedAmount?.allowsCustomAmount ?? false,
    )

    return {
      bookingNumber,
      paymentmethod: paymentMethod,
      paymentoption: paymentAmountType,
      ...paymentAmounts,
      groupUserId,
      contextitemid: contextItemId,
      referrerUrl: location.href(),
    }
  }

  function buildInitPaymentRequest(adyenPaymentObject: unknown): InitPaymentRequest {
    const { paymentOptions, selectedAmountType, customDepositAmount, selectedPaymentMethodType } = getDynamicValues()
    const selectedAmount = getSelectedAmount(paymentOptions.paymentAmounts, selectedAmountType)
    const paymentAmounts = resolvePaymentAmounts(
      selectedAmount,
      customDepositAmount,
      selectedAmount?.allowsCustomAmount ?? false,
      selectedPaymentMethodType,
      groupUserId !== null,
    )
    const paymentMethod = resolvePaymentMethodType(selectedPaymentMethodType) ?? ''
    const paymentAmountType = resolveSelectedAmountType(
      selectedAmountType,
      selectedAmount?.allowsCustomAmount ?? false,
    )

    return {
      bookingNumber,
      paymentMethodBrand: paymentMethod,
      paymentOption: paymentAmountType,
      ...paymentAmounts,
      groupUserId,
      contextitemid: contextItemId,
      referrerUrl: location.href(),
      adyenPaymentObject: JSON.stringify(adyenPaymentObject),
    }
  }

  function buildSubmitAdditionalDetailsRequest(adyenAdditionalDetails: unknown): SubmitAdditionalDetailsRequest {
    return {
      adyenAdditionalDetails: JSON.stringify(adyenAdditionalDetails),
      bookingIdentifier: bookingNumber,
      channel: 'Web',
    }
  }

  function redirectToSuccessUrl(successUrl: string) {
    const url = new URL(successUrl, location.origin())

    if (url.origin !== location.origin()) {
      const error = new Error('Invalid success URL returned by the server')
      error.name = INVALID_SUCCESS_URL_ERROR_NAME
      throw error
    }

    location.redirectTo(url.toString())
  }

  function handlePaymentResponse(response: AdyenPaymentResponse, actions?: AdyenFlowActions) {
    setAdyenReferences(response)
    adyenPaymentFailureDetails = extractAdyenPaymentFailureDetails(response)

    if (response.action) {
      store.getState().setIn('status', AdyenPaymentStatusValues.READY)

      // adyen-web 6 performs the action itself once the flow is resolved, so
      // handling it manually is only needed when no actions object is supplied.
      if (actions) {
        actions.resolve(response)
      }
      else {
        adyenClient.handleAction(response.action)
      }

      return
    }

    const paymentResult = resolvePaymentResultFromResultCode(response.resultCode)

    if (hidesPaymentOptions(paymentResult)) {
      const { successUrl } = store.getState()

      if (successUrl) {
        redirectToSuccessUrl(successUrl)
      }
      else {
        completeWith(paymentResult)
      }

      actions?.resolve(response)
      return
    }

    completeWith(paymentResult)
    actions?.resolve(response)
    if (requiresPaymentMethodReselection(response.resultCode)) {
      unselectPaymentMethod()
    }
  }

  async function paymentSubmitted(submitState: AdyenSubmitState, actions: AdyenFlowActions, mountToken: ActiveMountState | null) {
    if (!submitState.isValid || store.getState().status === AdyenPaymentStatusValues.SUBMITTING) {
      if (!submitState.isValid) {
        logAdyenEvent(AdyenTelemetryEventValues.SUBMIT_BLOCKED_BY_VALIDATION)
        analyticsClient.sendEvent(createPaymentEvent({
          description: 'payment submission',
          element: 'payment form',
          type: getDynamicValues().selectedPaymentMethodType ?? 'unknown',
          result: 'validation error',
          interaction: true,
          context,
        }))
      }
      actions.reject?.()
      return
    }

    const state = store.getState()
    state.setIn('status', AdyenPaymentStatusValues.SUBMITTING)
    state.setIn('paymentResult', null)
    adyenPaymentFailureDetails = {}

    try {
      const { paymentOptions, selectedAmountType, customDepositAmount, selectedPaymentMethodType } = getDynamicValues()
      const selectedAmount = getSelectedAmount(paymentOptions.paymentAmounts, selectedAmountType)
      const amountPaid = getAmountWithFeesValue(selectedAmount, customDepositAmount, selectedPaymentMethodType) ?? 0

      const paymentInitRequest = buildInitPaymentRequest(submitState.data)
      const { paymentOption: paymentType } = paymentInitRequest

      analyticsClient.sendEvent(createPaymentMethodEvent({
        paymentMethod: selectedPaymentMethodType ?? '',
        paymentType,
        amountPaid,
        manual: 'no',
      }))

      const response = await apiClient.initPayment(paymentInitRequest)

      if (mountToken?.cancelled) {
        actions.reject?.()
        return
      }

      handlePaymentResponse(response, actions)
    }
    catch (error) {
      if (mountToken?.cancelled) {
        actions.reject?.()
        return
      }
      logAdyenError(
        error,
        resolveAdyenErrorCode(error, AdyenErrorCodeValues.PAYMENT_SUBMISSION_EXCEPTION),
      )
      completeWith(PaymentResultValues.EXCEPTION)
      actions.reject?.()
      unselectPaymentMethod()
    }
  }

  async function additionalDetailsSubmitted(
    detailsState: AdyenAdditionalDetailsState,
    actions: AdyenFlowActions,
    mountToken: ActiveMountState | null,
  ) {
    if (store.getState().status === AdyenPaymentStatusValues.SUBMITTING) {
      actions.reject?.()
      return
    }

    const state = store.getState()
    state.setIn('status', AdyenPaymentStatusValues.SUBMITTING)
    state.setIn('paymentResult', null)
    adyenPaymentFailureDetails = {}

    try {
      const response = await apiClient.submitAdditionalDetails(buildSubmitAdditionalDetailsRequest(detailsState.data))

      if (mountToken?.cancelled) {
        actions.reject?.()
        return
      }

      handlePaymentResponse(response, actions)
    }
    catch (error) {
      if (mountToken?.cancelled) {
        actions.reject?.()
        return
      }

      logAdyenError(
        error,
        resolveAdyenErrorCode(error, AdyenErrorCodeValues.ADDITIONAL_DETAILS_SUBMISSION_EXCEPTION),
      )
      completeWith(PaymentResultValues.EXCEPTION)
      actions.reject?.()
      unselectPaymentMethod()
    }
  }

  async function paymentComponentMounted(element: HTMLElement) {
    if (activeMount) {
      activeMount.cancelled = true
      activeMount.abortController.abort()
    }
    const mountToken: ActiveMountState = {
      cancelled: false,
      abortController: new AbortController(),
    }
    activeMount = mountToken

    const state = store.getState()

    if (!isAdyenProvider()) {
      state.setIn('status', AdyenPaymentStatusValues.IDLE)
      return
    }

    const { paymentOptions, selectedPaymentMethodType, selectedAmountType, paymentMethodRow } = getDynamicValues()

    const selectedPaymentMethod = resolveSelectedPaymentMethod(paymentOptions, selectedPaymentMethodType)

    if (!selectedPaymentMethod || selectedPaymentMethod.transactionType !== ONLINE_TRANSACTION_TYPE || !selectedAmountType) {
      state.setIn('status', AdyenPaymentStatusValues.IDLE)
      return
    }

    state.setIn('status', AdyenPaymentStatusValues.LOADING)

    try {
      const parameters = await apiClient.createPaymentParameters(
        buildCreatePaymentParametersRequest(),
        mountToken.abortController.signal,
      )

      if (mountToken.cancelled) {
        return
      }

      store.getState().setIn('successUrl', parameters.successUrl ?? null)

      await adyenClient.mount({
        element,
        config: parameters.adyen,
        componentType: getPaymentMethodType(paymentMethodRow),
        configKey: getPaymentMethodConfigKey(paymentMethodRow),
        onSubmit: (submitState, _component, actions) => {
          paymentSubmitted(submitState, actions, mountToken)
        },
        onAdditionalDetails: (additionalState, _component, actions) => additionalDetailsSubmitted(additionalState, actions, mountToken),
        onFieldValid: (data) => {
          if (!data.valid) {
            logAdyenEvent(AdyenTelemetryEventValues.CARD_FIELD_INVALID, {
              fieldType: data.fieldType,
              encryptedFieldName: data.encryptedFieldName,
            })
          }
        },
        onActionHandled: (data) => {
          logAdyenEvent(AdyenTelemetryEventValues.ACTION_HANDLED, {
            componentType: data.componentType,
            actionDescription: data.actionDescription,
          })
        },
        onPaymentFailed: (error) => {
          const details = {
            ...adyenPaymentFailureDetails,
            ...extractAdyenPaymentFailureDetails(error),
          }
          const failure = error instanceof Error || Object.keys(details).length === 0 ? error : details

          logAdyenError(
            failure,
            AdyenErrorCodeValues.PAYMENT_FAILED,
            {
              adyenResultCode: details.resultCode,
              adyenRefusalReason: details.refusalReason,
              adyenRefusalReasonCode: details.refusalReasonCode,
            },
          )
        },
        onError: (error) => {
          logAdyenError(error, AdyenErrorCodeValues.COMPONENT_ERROR, {
            adyenErrorType: error instanceof Error ? error.name : undefined,
          })
        },
      })

      if (mountToken.cancelled) {
        return
      }

      store.getState().setIn('status', AdyenPaymentStatusValues.READY)
    }
    catch (error) {
      if (mountToken.cancelled) {
        return
      }
      logAdyenError(
        error,
        resolveAdyenErrorCode(error, AdyenErrorCodeValues.PAYMENT_MOUNT_EXCEPTION),
      )
      analyticsClient.sendEvent(createPaymentEvent({
        description: 'payment error',
        element: 'payment component',
        type: 'mount',
        result: 'payment component unavailable',
        interaction: false,
        context,
      }))
      completeWith(PaymentResultValues.EXCEPTION, AdyenPaymentStatusValues.IDLE, false)
      adyenClient.remove()
      unselectPaymentMethod()
    }
  }

  function paymentComponentUnmounted() {
    if (activeMount) {
      activeMount.cancelled = true
      activeMount.abortController.abort()
      activeMount = null
    }
    adyenClient.remove()
    adyenPspReference = undefined
    adyenMerchantReference = undefined
    adyenPaymentFailureDetails = {}
    const state = store.getState()
    state.setIn('status', AdyenPaymentStatusValues.IDLE)
    state.setIn('successUrl', null)
  }

  return {
    paymentComponentMounted,
    paymentSubmitted,
    additionalDetailsSubmitted,
    paymentComponentUnmounted,
  }
}
